import { TFunction } from 'react-i18next';
import { Payment, PaymentStatus } from '../types/payment';
import { Document } from '../types/document';
import { ChangeRequest, ChangeRequestStatus } from '../types/changeRequest';
import { enumMatches, tryNormalizeEnumValue } from './enumNormalize';
import { formatChangeRequestFieldName } from './formatChangeField';
import {
  IndividualMemberDto,
  MembershipType,
  VignetteStatus,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
  DisplayLanguage,
} from '../types/member';
import { User, CommunicationLanguage } from '../types/auth';
import {
  MemberDashboardViewModel,
  DashboardActivityItem,
  DashboardUrgentNotice,
  ProfileFieldKey,
} from '../types/dashboard';
import { memberDtoToFormData } from './memberForm';
import { computeOverallCompleteness } from './profileSectionCompleteness';
import {
  formatAthensActivityTimestamp,
  formatAthensDate,
  getAthensGreetingPeriod,
} from './athensTime';
import { parseMemberCardNumber } from './memberCardNumber';
import {
  getVignetteStatusLabel,
  resolveVignetteStatus as resolveSharedVignetteStatus,
} from './vignette';

export const PROFILE_FIELD_LABEL_KEYS: Record<ProfileFieldKey, string> = {
  firstName: 'profile.firstName',
  lastName: 'profile.lastName',
  email: 'profile.email',
  dateOfBirth: 'profile.dateOfBirth',
  residentialAddress: 'profile.residentialAddress',
  phone: 'profile.phone',
  membershipType: 'profile.membershipType',
  workSector: 'profile.workSector',
};

/** Required readiness fields (date of birth is optional enrichment, not readiness). */
const REQUIRED_PROFILE_FIELDS: ProfileFieldKey[] = [
  'firstName',
  'lastName',
  'email',
  'residentialAddress',
  'phone',
  'membershipType',
  'workSector',
];

function getLocale(language: string): string {
  return language === 'gr' || language.startsWith('gr') ? 'el-GR' : 'en-GB';
}

function toTitleCase(value: string): string {
  if (!value) return value;
  return value
    .split(/\s+/)
    .map(word => (word.length ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
}

/** Greek vocative for greeting: strip trailing sigma (σ / Σ / final ς). */
function toVocative(name: string): string {
  if (!name) return name;
  const last = name.slice(-1);
  if (last === 'Σ' || last === 'σ' || last === 'ς') return name.slice(0, -1);
  return name;
}

export function formatCurrency(amount: number, locale: string): string {
  return new Intl.NumberFormat(getLocale(locale), {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

export function formatDate(dateStr: string | undefined, locale: string): string {
  return formatAthensDate(dateStr, locale);
}

export function formatActivityTimestamp(timestamp: string, locale: string): string {
  return formatAthensActivityTimestamp(timestamp, locale);
}

function paymentDueDate(year: number): Date {
  return new Date(year, 11, 31);
}

function resolveGreetingFirstName(
  member: IndividualMemberDto | null,
  preferredLang: DisplayLanguage
): string {
  if (!member) return '';

  const firstName = getMemberDisplayFirstName(member, preferredLang).trim();
  const lastName = getMemberDisplayLastName(member, preferredLang).trim();
  if (firstName) return firstName;

  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName.split(/\s+/)[0];

  return '';
}

function membershipTypeBadge(member: IndividualMemberDto | null, t: TFunction): string {
  if (!member?.membershipType) {
    return t('dashboard.roles.individualMember');
  }

  switch (member.membershipType) {
    case MembershipType.Student:
      return t('dashboard.membershipBadge.student');
    case MembershipType.Retired:
      return t('dashboard.membershipBadge.retired');
    case MembershipType.Honorary:
      return t('dashboard.membershipBadge.honorary');
    case MembershipType.Regular:
    default:
      return t('dashboard.membershipBadge.regular');
  }
}

function changeRequestFieldLabel(fieldName: string, t: TFunction): string {
  return formatChangeRequestFieldName(fieldName, t);
}

function truncateFilename(name: string, max = 40): string {
  if (name.length <= max) return name;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  const base = name.slice(0, max - ext.length - 1);
  return `${base}…${ext}`;
}

function calculateProfileCompletion(
  member: IndividualMemberDto | null,
  t: TFunction
): {
  percentage: number;
  missingFieldKeys: ProfileFieldKey[];
  missingFieldLabels: string[];
  completedFieldKeys: ProfileFieldKey[];
  completedFieldLabels: string[];
} {
  if (!member) {
    return {
      percentage: 0,
      missingFieldKeys: [...REQUIRED_PROFILE_FIELDS],
      missingFieldLabels: REQUIRED_PROFILE_FIELDS.map(k => t(PROFILE_FIELD_LABEL_KEYS[k])),
      completedFieldKeys: [],
      completedFieldLabels: [],
    };
  }

  const firstName = member.name?.firstNameEn ?? member.name?.firstNameGr ?? '';
  const lastName = member.name?.lastNameEn ?? member.name?.lastNameGr ?? '';
  const residentialAddress = member.residential?.addressEn ?? member.residential?.addressGr ?? '';

  const fieldValues: Record<ProfileFieldKey, string> = {
    firstName,
    lastName,
    email: member.email ?? '',
    dateOfBirth: member.dateOfBirth ?? '',
    residentialAddress,
    phone: member.phone ?? '',
    membershipType: member.membershipType != null ? String(member.membershipType) : '',
    workSector: member.workSector != null ? String(member.workSector) : '',
  };

  const completedFieldKeys: ProfileFieldKey[] = [];
  const missingFieldKeys: ProfileFieldKey[] = [];

  REQUIRED_PROFILE_FIELDS.forEach(field => {
    const value = fieldValues[field];
    if (value !== null && value !== undefined && value !== '') {
      completedFieldKeys.push(field);
    } else {
      missingFieldKeys.push(field);
    }
  });

  // Same scoring model as Profile Edit (section-weighted overall; DOB excluded).
  const formLike = memberDtoToFormData(member, [], CommunicationLanguage.English);
  const percentage = computeOverallCompleteness(formLike).percentage;

  return {
    percentage,
    missingFieldKeys,
    missingFieldLabels: missingFieldKeys.map(k => t(PROFILE_FIELD_LABEL_KEYS[k])),
    completedFieldKeys,
    completedFieldLabels: completedFieldKeys.map(k => t(PROFILE_FIELD_LABEL_KEYS[k])),
  };
}

function resolveVignetteStatus(member: IndividualMemberDto | null): VignetteStatus {
  return resolveSharedVignetteStatus(member);
}

function vignetteStatusLabel(status: VignetteStatus, t: TFunction): string {
  return getVignetteStatusLabel(status, t);
}

function generateActivities(
  payments: Payment[],
  documents: Document[],
  changeRequests: ChangeRequest[],
  t: TFunction,
  locale: string
): DashboardActivityItem[] {
  const activities: DashboardActivityItem[] = [];

  payments.slice(0, 3).forEach(payment => {
    const isPaid = payment.status === PaymentStatus.Paid;
    activities.push({
      id: `payment-${payment.id}`,
      type: 'payment',
      title: isPaid ? t('dashboard.activity.paymentPaid') : t('dashboard.activity.paymentPending'),
      description: isPaid
        ? t('dashboard.activity.paymentPaidDescription', {
            amount: formatCurrency(payment.amount, locale),
            year: payment.year,
          })
        : t('dashboard.activity.paymentPendingDescription', {
            amount: formatCurrency(payment.amount, locale),
            year: payment.year,
          }),
      timestamp: payment.paidAt || payment.createdAt,
      formattedTimestamp: formatActivityTimestamp(payment.paidAt || payment.createdAt, locale),
      link: '/payments',
      statusLabel: isPaid ? t('dashboard.subscription.paid') : t('dashboard.subscription.pending'),
      statusVariant: isPaid ? 'success' : 'warning',
    });
  });

  documents.slice(0, 3).forEach(doc => {
    activities.push({
      id: `document-${doc.id}`,
      type: 'document',
      title: doc.isVerified
        ? t('dashboard.activity.documentVerified')
        : t('dashboard.activity.documentUploaded'),
      description: t('dashboard.activity.documentDescription', {
        name: truncateFilename(doc.originalFileName),
      }),
      fullDescription: doc.originalFileName,
      timestamp: doc.uploadedAt,
      formattedTimestamp: formatActivityTimestamp(doc.uploadedAt, locale),
      link: '/documents',
      statusLabel: doc.isVerified ? t('dashboard.verified') : t('dashboard.pending'),
      statusVariant: doc.isVerified ? 'success' : 'warning',
    });
  });

  changeRequests.slice(0, 3).forEach(request => {
    let title: string;
    let variant: 'success' | 'warning' | 'error' = 'warning';
    const status = tryNormalizeEnumValue(ChangeRequestStatus, request.status);
    if (status === ChangeRequestStatus.Approved) {
      title = t('dashboard.activity.changeRequestApproved');
      variant = 'success';
    } else if (status === ChangeRequestStatus.Rejected) {
      title = t('dashboard.activity.changeRequestRejected');
      variant = 'error';
    } else {
      title = t('dashboard.activity.changeRequestPending');
    }
    activities.push({
      id: `change-${request.id}`,
      type: 'changeRequest',
      title,
      description: t('dashboard.activity.changeRequestDescription', {
        field: changeRequestFieldLabel(request.fieldName, t),
      }),
      timestamp: request.requestedAt,
      formattedTimestamp: formatActivityTimestamp(request.requestedAt, locale),
      link: '/change-requests',
      statusVariant: variant,
    });
  });

  return activities
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 5);
}

export interface DashboardRawData {
  user: User;
  payments: Payment[];
  documents: Document[];
  changeRequests: ChangeRequest[];
  member: IndividualMemberDto | null;
  language: string;
}

export function buildMemberDashboardViewModel(
  data: DashboardRawData,
  t: TFunction
): MemberDashboardViewModel {
  const { user, payments, documents, changeRequests, member, language } = data;
  const locale = language;
  const currentYear = new Date().getFullYear();
  const currentPayment = payments.find(p => p.year === currentYear) ?? null;

  const preferredLang: DisplayLanguage = locale === 'gr' ? 'gr' : 'en';
  const rawFirstName = resolveGreetingFirstName(member, preferredLang);
  const displayFirstName = rawFirstName ? toTitleCase(rawFirstName) : '';
  const displayLastName =
    (member ? getMemberDisplayLastName(member, preferredLang) : '') || '';

  const greetingName =
    locale === 'gr' && displayFirstName
      ? toVocative(displayFirstName)
      : displayFirstName;

  const openChangeRequests = changeRequests.filter(cr =>
    enumMatches(ChangeRequestStatus, cr.status, ChangeRequestStatus.Pending)
  ).length;
  const verifiedDocuments = documents.filter(d => d.isVerified).length;
  const unverifiedDocuments = documents.filter(d => !d.isVerified).length;

  const profile = calculateProfileCompletion(member, t);
  const vignetteStatus = resolveVignetteStatus(member);

  const urgentNotices: DashboardUrgentNotice[] = [];

  if (user && 'emailConfirmed' in user && !user.emailConfirmed) {
    urgentNotices.push({
      type: 'warning',
      title: t('dashboard.emailNotConfirmed'),
      message: t('dashboard.emailNotConfirmedMessage'),
    });
  }

  const membershipBadge = membershipTypeBadge(member, t);

  const dueDateLabel =
    currentPayment && currentPayment.status === PaymentStatus.Pending
      ? t('dashboard.subscription.dueBy', {
          date: formatDate(paymentDueDate(currentPayment.year).toISOString(), locale),
        })
      : null;

  const greetingPeriod = getAthensGreetingPeriod();
  const greetingKey =
    greetingPeriod === 'morning'
      ? 'dashboard.greetingMorning'
      : greetingPeriod === 'afternoon'
        ? 'dashboard.greetingAfternoon'
        : 'dashboard.greetingEvening';

  return {
    greeting: displayFirstName
      ? t(greetingKey, {
          name: greetingName,
          defaultValue:
            greetingPeriod === 'morning'
              ? `Good morning, ${greetingName}`
              : greetingPeriod === 'afternoon'
                ? `Good afternoon, ${greetingName}`
                : `Good evening, ${greetingName}`,
        })
      : t('dashboard.greetingFallback'),
    greetingFirstName: greetingName,
    portalDescription: t('dashboard.portalDescription'),
    subscription: {
      year: currentYear,
      payment: currentPayment,
      isPaid: currentPayment?.status === PaymentStatus.Paid,
      isPending: currentPayment?.status === PaymentStatus.Pending,
      formattedAmount: currentPayment
        ? formatCurrency(currentPayment.amount, locale)
        : null,
      paidOnLabel: currentPayment?.paidAt
        ? t('dashboard.subscription.paidOn', {
            date: formatDate(currentPayment.paidAt, locale),
          })
        : null,
      dueDateLabel,
      statusLabel: currentPayment
        ? currentPayment.status === PaymentStatus.Paid
          ? t('dashboard.subscription.paid')
          : t('dashboard.subscription.pending')
        : null,
      hasReceipt: currentPayment?.hasReceipt ?? false,
      showPayNow: currentPayment?.status === PaymentStatus.Pending,
    },
    identity: {
      fullName: toTitleCase(`${displayFirstName} ${displayLastName}`.trim()) || user.email,
      email: user.email ?? member?.email ?? '',
      ...(() => {
        const parsedCard = parseMemberCardNumber(member?.memberCardNumber);
        return {
          memberNumber: parsedCard.cardNumber || null,
          firstRegisteredFromCard: parsedCard.firstRegisteredYear,
        };
      })(),
      membershipBadge,
      avatarName: `${displayFirstName} ${displayLastName}`.trim() || user.email || '?',
    },
    overview: {
      documentsCount: documents.length,
      documentsSupporting: t('dashboard.overview.documentsSupporting', {
        verified: verifiedDocuments,
        pending: unverifiedDocuments,
      }),
      openRequestsCount: openChangeRequests,
      totalRequestsCount: changeRequests.length,
      totalRequestsLabel:
        changeRequests.length > 0
          ? t('dashboard.overview.totalRequestsShort', { count: changeRequests.length })
          : null,
      profilePercentage: profile.percentage,
      missingFieldsCount: profile.missingFieldKeys.length,
      missingFieldsSupporting:
        profile.missingFieldKeys.length > 0
          ? t('dashboard.overview.missingFieldsCount', {
              count: profile.missingFieldKeys.length,
            })
          : t('dashboard.complete'),
      vignetteStatusLabel: vignetteStatusLabel(vignetteStatus, t),
      vignetteSupporting: member?.vignetteUpdatedAt
        ? `${t('vignette.lastUpdated')}: ${formatDate(member.vignetteUpdatedAt, locale)}`
        : null,
    },
    profileCompletion: {
      percentage: profile.percentage,
      missingFieldLabels: profile.missingFieldLabels,
      completedFieldLabels: profile.completedFieldLabels,
      remainingMessage: t('dashboard.profileCompletion.remaining', {
        count: profile.missingFieldKeys.length,
      }),
    },
    activities: generateActivities(payments, documents, changeRequests, t, locale),
    urgentNotices,
  };
}
